import { useMemo } from 'react';
import { PageType } from '@common/entities';

import { AnyField } from 'types/fields';
import { AnyPage, BlankPage } from 'types/page';

import { ConditionalElementState } from './conditionalElementState';
import { useEvaluateDisplayCondition } from './useEvaluateDisplayCondition';

/**
 * Hook to filter out hidden blank pages and return visible pages with count.
 * This ensures mobile and desktop navigation show consistent page counts.
 */
export function useVisiblePages(pages: AnyPage[]) {
  const evaluateDisplayCondition = useEvaluateDisplayCondition();

  const visiblePages = useMemo(() => {
    return pages.filter((page) => {
      // Only blank pages can be hidden based on display logic
      if (page.type !== PageType.Blank) {
        return true;
      }

      const blankPage = page as BlankPage;

      // Check if the blank page is fully hidden
      if (!blankPage.sections?.length) return false;

      // For each section, check if it or all its elements are hidden
      const isFullyHidden = blankPage.sections.every((section) => {
        // If section has display logic and evaluates to hidden, skip checking elements
        if (section.displayLogic) {
          const shouldDisplay = evaluateDisplayCondition(section.displayLogic);
          if (shouldDisplay === ConditionalElementState.Hidden) return true;
        }

        // Check if all elements in the section are hidden
        return section.elements.every((element: AnyField) => {
          if (!element.displayLogic) return false;
          return evaluateDisplayCondition(element.displayLogic) === ConditionalElementState.Hidden;
        });
      });

      return !isFullyHidden;
    });
  }, [pages, evaluateDisplayCondition]);

  return {
    visiblePages,
    visiblePageCount: visiblePages.length,
  };
}
